import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  FormControlLabel,
  Switch
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import SecurityIcon from '@mui/icons-material/Security';
import { apiFetch } from './api';

function UserManagement({ onBack }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  
  // Add user dialog
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newUsername, setNewUsername] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [newIsAdmin, setNewIsAdmin] = useState(false);
  
  // Edit user dialog
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);
  const [editUsername, setEditUsername] = useState('');
  const [editPassword, setEditPassword] = useState('');
  const [editIsAdmin, setEditIsAdmin] = useState(false);
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await apiFetch('/users');
      setUsers(data);
      setError(null);
    } catch (err) {
      setError(err.message || 'Fehler beim Laden der Benutzer');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleAddUser = async () => {
    if (!newUsername) {
      setError('Benutzername ist erforderlich');
      return;
    }

    try {
      setError(null);
      await apiFetch('/users', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: newUsername,
          password: newPassword || null,
          is_admin: newIsAdmin
        })
      });
      setSuccess(`Benutzer "${newUsername}" erfolgreich erstellt!`);
      setAddDialogOpen(false);
      setNewUsername('');
      setNewPassword('');
      setNewIsAdmin(false);
      await loadUsers();
    } catch (err) {
      setError(err.message || 'Fehler beim Erstellen des Benutzers');
    }
  };

  const handleEditUser = async () => {
    if (!editUser) return;

    try {
      setError(null);
      await apiFetch(`/users/${editUser.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          username: editUsername,
          password: editPassword || undefined,
          is_admin: editIsAdmin
        })
      });
      setSuccess(`Benutzer aktualisiert!`);
      setEditDialogOpen(false);
      setEditUser(null);
      await loadUsers();
    } catch (err) {
      setError(err.message || 'Fehler beim Aktualisieren des Benutzers');
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;

    try {
      setError(null);
      await apiFetch(`/users/${userToDelete.id}`, {
        method: 'DELETE'
      });
      setSuccess(`Benutzer "${userToDelete.username}" gelöscht!`);
      setDeleteDialogOpen(false);
      setUserToDelete(null);
      await loadUsers();
    } catch (err) {
      setError(err.message || 'Fehler beim Löschen des Benutzers');
    }
  };

  const openEditDialog = (user) => {
    setEditUser(user);
    setEditUsername(user.username);
    setEditPassword('');
    setEditIsAdmin(user.is_admin);
    setEditDialogOpen(true);
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: 2 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Benutzerverwaltung
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddDialogOpen(true)}
        >
          Neu
        </Button>
      </Box>

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell><strong>Name</strong></TableCell>
              <TableCell><strong>Rolle</strong></TableCell>
              <TableCell><strong>Auth</strong></TableCell>
              <TableCell align="right"><strong>Aktionen</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.map((user) => (
              <TableRow key={user.id}>
                <TableCell>{user.username}</TableCell>
                <TableCell>
                  {user.is_admin ? (
                    <Chip label="Admin" color="primary" size="small" />
                  ) : (
                    <Chip label="Benutzer" size="small" />
                  )}
                </TableCell>
                <TableCell>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap">
                    {user.has_password && (
                      <Chip 
                        label="Passwort" 
                        size="small" 
                        icon={<CheckCircleIcon />}
                        color="default"
                      />
                    )}
                    {user.has_webauthn && (
                      <Chip 
                        label="WebAuthn" 
                        size="small" 
                        icon={<SecurityIcon />}
                        color="success"
                      />
                    )}
                    {user.has_totp && (
                      <Chip 
                        label="TOTP" 
                        size="small" 
                        icon={<SecurityIcon />}
                        color="success"
                      />
                    )}
                    {!user.has_password && !user.has_webauthn && !user.has_totp && (
                      <Chip 
                        label="Keine" 
                        size="small" 
                        icon={<CancelIcon />}
                        color="error"
                      />
                    )}
                    {user.is_admin && !user.has_mfa && (
                      <Chip 
                        label="MFA erforderlich!" 
                        size="small" 
                        color="error"
                      />
                    )}
                  </Stack>
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    onClick={() => openEditDialog(user)}
                    color="primary"
                  >
                    <EditIcon />
                  </IconButton>
                  <IconButton
                    size="small"
                    onClick={() => {
                      setUserToDelete(user);
                      setDeleteDialogOpen(true);
                    }}
                    color="error"
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Box sx={{ mt: 3 }}>
        <Button variant="outlined" onClick={onBack}>
          Zurück
        </Button>
      </Box>

      {/* Add User Dialog */}
      <Dialog open={addDialogOpen} onClose={() => setAddDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Neuen Benutzer hinzufügen</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Benutzername"
              value={newUsername}
              onChange={(e) => setNewUsername(e.target.value)}
              sx={{ mb: 2 }}
              required
            />
            <TextField
              fullWidth
              label="Passwort (optional)"
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              helperText="Leer lassen wenn der Benutzer WebAuthn oder TOTP einrichten soll"
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={newIsAdmin}
                  onChange={(e) => setNewIsAdmin(e.target.checked)}
                />
              }
              label="Administrator"
            />
            {newIsAdmin && (
              <Alert severity="warning" sx={{ mt: 2 }}>
                Admin-Benutzer müssen MFA (WebAuthn oder TOTP) einrichten, bevor sie sich anmelden können.
              </Alert>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={handleAddUser} variant="contained">
            Hinzufügen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Benutzer bearbeiten</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Benutzername"
              value={editUsername}
              onChange={(e) => setEditUsername(e.target.value)}
              sx={{ mb: 2 }}
              required
            />
            <TextField
              fullWidth
              label="Neues Passwort (optional)"
              type="password"
              value={editPassword}
              onChange={(e) => setEditPassword(e.target.value)}
              helperText="Leer lassen, um Passwort unverändert zu lassen"
              sx={{ mb: 2 }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={editIsAdmin}
                  onChange={(e) => setEditIsAdmin(e.target.checked)}
                />
              }
              label="Administrator"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={handleEditUser} variant="contained">
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Benutzer löschen?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Möchten Sie den Benutzer "{userToDelete?.username}" wirklich löschen?
            Diese Aktion kann nicht rückgängig gemacht werden.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Abbrechen</Button>
          <Button onClick={handleDeleteUser} color="error" variant="contained">
            Löschen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default UserManagement;
